import React, { useState, useEffect } from 'react';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Lightbulb, 
  Plus, 
  Clock, 
  User,
  Calendar,
  Trash2,
  CheckCircle2,
  FileText,
  ArrowRight
} from 'lucide-react';

const SujetsAG = ({ user }) => {
  const [sujets, setSujets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ titre: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('tous');

  // Seuls président et secrétaire peuvent gérer les sujets
  const canManage = user?.role === 'président' || user?.role === 'secrétaire';

  useEffect(() => {
    fetchSujets();
  }, []);

  const fetchSujets = async () => {
    try {
      const response = await apiClient.get('/sujets-proposes');
      setSujets(response.data.sujets || []);
    } catch (error) {
      console.error('Erreur chargement sujets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.titre.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/sujets-proposes', formData);
      toast.success('Sujet proposé avec succès !');
      setShowModal(false);
      setFormData({ titre: '', description: '' });
      fetchSujets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la proposition'));
    } finally {
      setSubmitting(false);
    }
  };

  const addToFutureAG = async (sujetId) => {
    try {
      await apiClient.put(`/sujets-proposes/${sujetId}`, { statut: 'integre_ag' });
      toast.success('Sujet ajouté à la future AG');
      fetchSujets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de l\'ajout'));
    }
  };

  const deleteSujet = async (sujetId) => {
    if (!confirm('Supprimer ce sujet ?')) return;
    
    try {
      await apiClient.delete(`/sujets-proposes/${sujetId}`);
      toast.success('Sujet supprimé');
      fetchSujets();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  const getStatutBadge = (statut) => {
    const badges = {
      en_attente: { color: 'bg-yellow-100 text-yellow-700', label: 'En attente' },
      integre_ag: { color: 'bg-green-100 text-green-700', label: 'Intégré à l\'AG' },
      traite: { color: 'bg-blue-100 text-blue-700', label: 'Traité' }
    };
    const badge = badges[statut] || badges.en_attente;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {statut === 'integre_ag' ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredSujets = filter === 'tous' 
    ? sujets 
    : filter === 'en_attente' 
      ? sujets.filter(s => s.statut === 'en_attente')
      : sujets.filter(s => s.statut === 'integre_ag' || s.statut === 'traite');

  const stats = {
    total: sujets.length,
    en_attente: sujets.filter(s => s.statut === 'en_attente').length,
    integre: sujets.filter(s => s.statut === 'integre_ag' || s.statut === 'traite').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Lightbulb className="w-7 h-7 text-amber-500" />
            Propositions pour l'AG
          </h1>
          <p className="text-slate-500 mt-1">
            Proposez des sujets à aborder lors de la prochaine assemblée générale
          </p>
        </div>
        <Button 
          onClick={() => setShowModal(true)}
          className="bg-amber-500 hover:bg-amber-600"
          data-testid="propose-sujet-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Proposer un sujet
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
          <p className="text-sm text-slate-500">Total propositions</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-2xl font-bold text-yellow-600">{stats.en_attente}</p>
          <p className="text-sm text-slate-500">En attente</p>
        </div>
        <div className="bg-white rounded-xl p-4 border shadow-sm">
          <p className="text-2xl font-bold text-green-600">{stats.integre}</p>
          <p className="text-sm text-slate-500">Intégrées à l'AG</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: 'tous', label: 'Tous' },
          { key: 'en_attente', label: 'En attente' },
          { key: 'integre', label: 'Intégrés à l\'AG' }
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f.key
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste des sujets */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-xl p-8 border text-center">
            <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto"></div>
            <p className="text-slate-500 mt-4">Chargement...</p>
          </div>
        ) : filteredSujets.length === 0 ? (
          <div className="bg-white rounded-xl p-8 border text-center">
            <Lightbulb className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              {filter === 'tous' ? 'Aucun sujet proposé' : `Aucun sujet ${filter === 'en_attente' ? 'en attente' : 'intégré'}`}
            </h3>
            <p className="text-slate-500 mb-4">
              Soyez le premier à proposer un sujet pour la prochaine AG !
            </p>
            <Button 
              onClick={() => setShowModal(true)}
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              Proposer un sujet
            </Button>
          </div>
        ) : (
          filteredSujets.map((sujet) => (
            <div 
              key={sujet.id} 
              className="bg-white rounded-xl p-5 border shadow-sm hover:shadow-md transition-shadow"
              data-testid={`sujet-${sujet.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    {getStatutBadge(sujet.statut)}
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(sujet.created_at)}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">
                    {sujet.titre}
                  </h3>
                  
                  {sujet.description && (
                    <p className="text-slate-600 text-sm mb-3">
                      {sujet.description}
                    </p>
                  )}
                  
                  <p className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                    <User className="w-4 h-4" />
                    Proposé par {sujet.propose_par_nom}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {/* Bouton "Mettre dans future AG" - président/secrétaire uniquement */}
                  {canManage && sujet.statut === 'en_attente' && (
                    <Button
                      onClick={() => addToFutureAG(sujet.id)}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Mettre dans future AG
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                  
                  {/* Suppression (auteur ou admin) */}
                  {(sujet.propose_par === user?.id || canManage) && (
                    <button
                      onClick={() => deleteSujet(sujet.id)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal de proposition */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              Proposer un sujet
            </DialogTitle>
            <DialogDescription>
              Votre proposition sera visible par tous et pourra être intégrée à l'ordre du jour par le président ou le secrétaire.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Titre du sujet *
              </label>
              <input
                type="text"
                value={formData.titre}
                onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                placeholder="Ex: Refaire les chenaux côté OUEST"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                required
                data-testid="sujet-titre-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Description (optionnel)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Détaillez votre proposition si nécessaire..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none"
                data-testid="sujet-description-input"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={submitting || !formData.titre.trim()}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
                data-testid="submit-sujet-btn"
              >
                {submitting ? 'Envoi...' : 'Proposer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SujetsAG;
